import { FontIcons, OnlyForScreenReader } from '@brandfolder/react';
import { forwardRef, useContext, useEffect } from 'react';
import styled from 'styled-components';

import { BlockDrag } from '@components/blocks';
import { AsideContext } from '@context/aside';
import { DragAndDropContext } from '@context/drag-and-drop';
import { PageContext } from '@context/page';
import { BlockTypes } from '@enums/blocks';
import { usePanelSdkAttachmentHandlers } from '@hooks/use-panel-sdk-attachment-handlers';

import { AsideButtonWrapper } from './aside-button-wrapper';
import { AsideCloseButton } from './aside-close-button';
import { AsideDialog, OmittedAsideDialogProps } from './aside-dialog';
import { AsidePopout, HoverHelpPositions } from './aside-popout';
import { ColorsPopout } from './colors/colors-popout';
import { PanelUI } from './panel-ui';

interface AsideBlocksProps extends OmittedAsideDialogProps {}

const asideBlocksId = 'aside-blocks';

const UnstyledAsideBlocks = forwardRef<HTMLDivElement, AsideBlocksProps>((props, ref) => {
  const {
    setShowBlocks,
    setShowCodeBlock,
    setShowImage,
    setShowText,
    showCodeBlock,
    showImage,
    showText
  } = useContext(AsideContext);
  const { dragged } = useContext(DragAndDropContext);
  const { page } = useContext(PageContext);
  const { addAttachmentBlock } = usePanelSdkAttachmentHandlers();

  const handleEscClose = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      setShowBlocks(false);
    }
  };

  useEffect(() => {
    window.addEventListener('keyup', handleEscClose);

    return () => {
      window.removeEventListener('keyup', handleEscClose);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!page) return null;

  const openOnHover = dragged ? false : true;

  return (
    <AsideDialog {...props} id={asideBlocksId} ref={ref} title="">
      <>
        <AsideButtonWrapper level={2}>
          <AsidePopout
            content={
              <PanelUI
                onSelect={(attachmentDto) => {
                  addAttachmentBlock(attachmentDto, undefined, 0);
                }}
              />
            }
            hoverHelpPosition={HoverHelpPositions.Bottom}
            icon={FontIcons.Image}
            id={`${asideBlocksId}-assets`}
            label="Open add an asset block"
            open={showImage}
            openOnHover={openOnHover}
            setOpen={setShowImage}
            title="Assets"
            tooltip="Assets"
          />
        </AsideButtonWrapper>
        <AsideButtonWrapper level={2}>
          <AsidePopout
            content={<BlockDrag type={BlockTypes.Text}>Text</BlockDrag>}
            icon={FontIcons.FontType}
            height={178}
            hoverHelpPosition={HoverHelpPositions.Both}
            id={`${asideBlocksId}-text`}
            label="Open add a text block"
            open={showText}
            openOnHover={openOnHover}
            setOpen={setShowText}
            title="Text"
            tooltip="Text"
          />
        </AsideButtonWrapper>
        <AsideButtonWrapper level={2}>
          <AsidePopout
            content={<BlockDrag type={BlockTypes.Html}>HTML</BlockDrag>}
            height={178}
            hoverHelpPosition={HoverHelpPositions.Top}
            icon={FontIcons.Embed}
            id={`${asideBlocksId}-code`}
            label="Open add a code block"
            open={showCodeBlock}
            openOnHover={openOnHover}
            setOpen={setShowCodeBlock}
            title="Custom Code"
            tooltip="Code"
          />
        </AsideButtonWrapper>
        <AsideButtonWrapper level={2}>
          <ColorsPopout />
        </AsideButtonWrapper>
        <OnlyForScreenReader>
          <AsideButtonWrapper level={2}>
            <AsideCloseButton label="Close adding blocks" setOpen={setShowBlocks} tooltip="Close" />
          </AsideButtonWrapper>
        </OnlyForScreenReader>
      </>
    </AsideDialog>
  );
});

const StyledAsideBlocks = styled(UnstyledAsideBlocks)`
  #${asideBlocksId}-assets-popout {
    .bf-popout__focus-lock {
      height: 100%;
      display: flex;
      flex-direction: column;

      .bf-popout__body {
        padding: 0;
        flex: 1 1 100%;

        .bf-popout__autofocus {
          height: 100%;
        }
      }
    }
  }
`;

export const AsideBlocks = forwardRef<HTMLDivElement, AsideBlocksProps>((props, ref) => {
  return <StyledAsideBlocks {...props} ref={ref} />;
});
